type Messages = Record<'postcode' | 'required' | 'email' | 'phone' | 'failed' | 'sending', string>;
type Step = '1' | '2' | 'done';

declare global { interface Window { dataLayer?: Record<string, unknown>[] } }

const POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^(?:\+44|0)\d{9,10}$/;

const ATTRIBUTION = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];
const ATTRIBUTION_KEY = 'financiable-attribution';

// Campaign params from the landing URL, kept for the session so they survive navigating to another page.
const attribution = (): Record<string, string> => {
	const params = new URLSearchParams(location.search);
	const found = Object.fromEntries(ATTRIBUTION.filter((key) => params.get(key)).map((key) => [key, params.get(key)!]));
	try {
		if (Object.keys(found).length) sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(found));
		return JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) ?? '{}');
	} catch {
		return found;
	}
};

const formatPostcode = (value: string) => {
	const compact = value.replace(/\s+/g, '').toUpperCase();
	return compact.length > 3 ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : compact;
};

function init(form: HTMLFormElement) {
	const messages: Messages = JSON.parse(form.dataset.messages ?? '{}');
	const steps = Object.fromEntries([...form.querySelectorAll<HTMLElement>('[data-step]')].map((el) => [el.dataset.step, el])) as Record<Step, HTMLElement>;
	const status = form.querySelector<HTMLElement>('.lead-form__status')!;
	const submit = form.querySelector<HTMLButtonElement>('.lead-form__submit')!;
	const dialog = form.querySelector<HTMLDialogElement>('.lead-dialog');
	const input = (name: string) => form.querySelector<HTMLInputElement>(`[name="${name}"]`)!;
	// The contact layout is a single step: it starts (and stays) on step 2, which includes the postcode.
	const contact = form.dataset.variant === 'contact';
	const modal = form.dataset.variant === 'modal';
	let current: Step = contact ? '2' : '1';

	// The header layout keeps both steps in its dialog; other layouts keep step 1 in place.
	const show = (step: Step, focus = true) => {
		current = step;
		for (const [key, el] of Object.entries(steps)) el.hidden = key !== step && !(dialog && !modal && key === '1');
		status.hidden = true;
		if (dialog) {
			if (step === '1') { if (dialog.open && !modal) dialog.close(); }
			else if (!dialog.open) dialog.showModal();
		}
		const target = step === '1' ? input('postcode') : step === '2' ? steps['2'].querySelector<HTMLElement>('.lead-form__title') : steps.done;
		if (focus && (step !== '1' || !modal || dialog?.open)) target?.focus();
	};

	const setError = (field: HTMLInputElement, message = '') => {
		const error = document.getElementById(field.getAttribute('aria-describedby') ?? '');
		field.setAttribute('aria-invalid', String(!!message));
		if (error) { error.textContent = message; error.hidden = !message; }
		return !message;
	};

	const checkPostcode = () => {
		const field = input('postcode');
		const ok = POSTCODE.test(field.value.trim());
		if (ok) {
			field.value = formatPostcode(field.value);
			form.querySelectorAll('[data-postcode-echo]').forEach((el) => { el.textContent = field.value; });
		}
		return setError(field, ok ? '' : messages.postcode);
	};

	const checkDetails = () => {
		const results = ['first_name', 'last_name', 'email', 'phone'].map((name) => {
			const field = input(name);
			const value = field.value.trim();
			if (!value) return setError(field, messages.required);
			if (name === 'email' && !EMAIL.test(value)) return setError(field, messages.email);
			if (name === 'phone' && !PHONE.test(value.replace(/[\s()-]/g, ''))) return setError(field, messages.phone);
			return setError(field);
		});
		if (contact) results.push(checkPostcode());
		form.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
		return results.every(Boolean);
	};

	const send = async () => {
		const data: Record<string, unknown> = { ...attribution(), ...Object.fromEntries(new FormData(form).entries()), page: location.pathname };
		submit.disabled = true;
		const label = submit.textContent;
		submit.textContent = messages.sending;
		try {
			const response = await fetch(form.dataset.endpoint ?? '', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
			if (!response.ok) throw new Error(String(response.status));
			show('done');
			(window.dataLayer ??= []).push({ event: 'lead_submitted', topic: data.topic ?? '', placement: data.placement ?? '' });
		} catch {
			status.textContent = messages.failed;
			status.hidden = false;
		} finally {
			submit.disabled = false;
			submit.textContent = label;
		}
	};

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		if (current === '1') { if (checkPostcode()) show('2'); else input('postcode').focus(); }
		else if (current === '2' && checkDetails()) void send();
	});

	form.querySelector('[data-action="close"]')?.addEventListener('click', () => modal ? dialog?.close() : show('1'));

	// Esc (or the browser closing the dialog) returns to the postcode field.
	dialog?.addEventListener('close', () => { if (current !== '1') show('1', !modal); });
	dialog?.addEventListener('click', (event) => {
		if (event.target !== dialog || !dialog) return;
		if (modal) dialog.close();
		else show('1');
	});

	// Clear an error as soon as the field is corrected.
	form.addEventListener('input', (event) => {
		const field = event.target as HTMLInputElement;
		if (field.getAttribute('aria-invalid') === 'true') setError(field);
	});
}

attribution();
document.querySelectorAll<HTMLFormElement>('.lead-form').forEach(init);

export {};
